import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, X } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Button } from '../ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Skeleton } from '../ui/skeleton'
import { ExerciseResultsList } from '../training-diary/ExerciseResultsList'
import { exercisesService } from '../../services/exercises'
import { exerciseCategoriesService } from '../../services/exercise-categories'
import type { Exercise, ExerciseCategory } from '../../types/exercise'

interface ExercisePickerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Exercise ids already in the plan; hidden from the results. */
  excludeIds?: string[]
  onConfirm: (exercises: Exercise[]) => void
}

const CATEGORY_ALL = '__all__'

/**
 * Search + category-filtered multi-select for exercises. Mirrors the diary's
 * AddWorkoutDialog but returns the full Exercise objects (the plan matrix needs
 * the title/category to render new rows immediately).
 */
export function ExercisePickerDialog({
  open,
  onOpenChange,
  excludeIds = [],
  onConfirm,
}: ExercisePickerDialogProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [categoryId, setCategoryId] = useState<string>(CATEGORY_ALL)
  const [categories, setCategories] = useState<ExerciseCategory[]>([])
  const [results, setResults] = useState<Exercise[]>([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<Exercise[]>([])
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!open || categories.length > 0) return
    exerciseCategoriesService.list().then(setCategories).catch(() => {})
  }, [open, categories.length])

  useEffect(() => {
    if (!open) {
      setQuery('')
      setCategoryId(CATEGORY_ALL)
      setResults([])
      setSelected([])
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(async () => {
      setLoading(true)
      try {
        const params: { q?: string; category_id?: string } = {}
        if (query.trim()) params.q = query.trim()
        if (categoryId !== CATEGORY_ALL) params.category_id = categoryId
        const list = await exercisesService.search(params)
        setResults(list)
      } finally {
        setLoading(false)
      }
    }, 300)
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current)
    }
  }, [query, categoryId, open])

  function toggleSelection(exercise: Exercise) {
    setSelected((prev) =>
      prev.some((e) => e.id === exercise.id)
        ? prev.filter((e) => e.id !== exercise.id)
        : [...prev, exercise]
    )
  }

  function removeSelected(id: string) {
    setSelected((prev) => prev.filter((e) => e.id !== id))
  }

  function handleConfirm() {
    if (selected.length === 0) return
    onConfirm(selected)
    onOpenChange(false)
  }

  const visibleResults = results.filter((e) => !excludeIds.includes(e.id))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('trainingDiary.workouts.dialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('trainingDiary.workouts.dialog.searchPlaceholder')}
              className="pl-9"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('trainingDiary.workouts.dialog.categoryLabel')}</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={CATEGORY_ALL}>
                  {t('trainingDiary.workouts.dialog.categoryAll')}
                </SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {selected.length > 0 && (
          <div className="flex flex-col gap-1.5 rounded-md border bg-muted/40 p-2">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {t('trainingDiary.workouts.dialog.selectedLabel', { count: selected.length })}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {selected.map((ex) => (
                <button
                  key={ex.id}
                  type="button"
                  onClick={() => removeSelected(ex.id)}
                  className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs pl-2 pr-1 py-0.5 hover:bg-primary/20 transition-colors max-w-[200px]"
                >
                  <span className="truncate">{ex.title}</span>
                  <X className="size-3 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2 overflow-y-auto -mx-2 px-2 min-h-[200px]">
          {loading && (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-md" />
              ))}
            </div>
          )}

          {!loading && visibleResults.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              {t('trainingDiary.workouts.dialog.empty')}
            </p>
          )}

          {!loading && (
            <ExerciseResultsList
              exercises={visibleResults}
              selectedIds={selected.map((e) => e.id)}
              onToggle={toggleSelection}
            />
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" disabled={selected.length === 0} onClick={handleConfirm}>
            {t('trainingDiary.workouts.dialog.confirmAdd', { count: selected.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
